/* ==========================================================================
   GofTech Theme — theme.js
   Vanilla JS. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var Theme = window.Theme || {};
  window.Theme = Theme;

  Theme.settings = window.themeSettings || {};
  Theme.strings = window.themeStrings || {};
  Theme.routes = window.themeRoutes || {
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_update_url: '/cart/update',
    cart_url: '/cart',
    predictive_search_url: '/search/suggest'
  };

  /* ---------------- Helpers ---------------- */

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait || 250);
    };
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var fmt = format || Theme.settings.moneyFormat || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultTo(num, size) {
      num = num.toString();
      while (num.length < size) num = '0' + num;
      return num;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision === undefined ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number === null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cts = parts[1] ? decimal + parts[1] : '';
      return dollars + cts;
    }

    var match = fmt.match(placeholderRegex);
    switch (match && match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' '); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'"); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }
  Theme.formatMoney = formatMoney;

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) { data.__error = true; data.__status = r.status; }
        return data;
      });
    });
  }

  function trapFocus(container) {
    var focusables = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return function () {};
    var first = focusables[0], last = focusables[focusables.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    setTimeout(function () { first.focus(); }, 60);
    return function () { container.removeEventListener('keydown', handler); };
  }

  /* ---------------- Toast ---------------- */

  var Toast = {
    stack: null,
    ensure: function () {
      if (!this.stack) {
        this.stack = document.createElement('div');
        this.stack.className = 'toast-stack';
        document.body.appendChild(this.stack);
      }
      return this.stack;
    },
    show: function (message, type, duration) {
      var el = document.createElement('div');
      el.className = 'toast' + (type ? ' toast--' + type : '');
      el.setAttribute('role', 'status');
      el.innerHTML = '<span>' + message + '</span>';
      this.ensure().appendChild(el);
      setTimeout(function () {
        el.style.transition = 'opacity .3s ease, transform .3s ease';
        el.style.opacity = '0';
        el.style.transform = 'translateY(10px)';
        setTimeout(function () { el.remove(); }, 320);
      }, duration || 3000);
    }
  };
  Theme.Toast = Toast;

  /* ---------------- Body scroll lock ---------------- */

  var scrollLock = {
    count: 0,
    y: 0,
    on: function () {
      if (this.count === 0) {
        this.y = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = -this.y + 'px';
        document.body.style.width = '100%';
      }
      this.count++;
    },
    off: function () {
      this.count = Math.max(0, this.count - 1);
      if (this.count === 0) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, this.y);
      }
    }
  };

  /* ---------------- Drawer ---------------- */

  function Drawer(el) {
    this.el = el;
    this.releaseFocus = null;
    var self = this;
    $$('[data-drawer-close]', el).forEach(function (btn) {
      btn.addEventListener('click', function () { self.close(); });
    });
    var overlay = $('.drawer__overlay', el);
    if (overlay) overlay.addEventListener('click', function () { self.close(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.classList.contains('is-open')) self.close();
    });
  }
  Drawer.prototype.open = function () {
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    scrollLock.on();
    this.releaseFocus = trapFocus(this.el);
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { drawer: this.el } }));
  };
  Drawer.prototype.close = function () {
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    scrollLock.off();
    if (this.releaseFocus) this.releaseFocus();
  };
  Drawer.prototype.toggle = function () {
    this.el.classList.contains('is-open') ? this.close() : this.open();
  };

  Theme.drawers = {};
  function initDrawers() {
    $$('[data-drawer]').forEach(function (el) {
      Theme.drawers[el.getAttribute('data-drawer')] = new Drawer(el);
    });
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-drawer-open]');
      if (!trigger) return;
      e.preventDefault();
      var name = trigger.getAttribute('data-drawer-open');
      if (Theme.drawers[name]) Theme.drawers[name].open();
    });
  }

  /* ---------------- Cart ---------------- */

  var Cart = {
    state: null,

    get: function () {
      return fetchJSON(Theme.routes.cart_url + '.js');
    },

    add: function (items, opts) {
      opts = opts || {};
      var body = { items: Array.isArray(items) ? items : [items] };
      if (opts.sections) body.sections = opts.sections;
      return fetchJSON(Theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (data) {
        if (data.__error || data.status === 422) {
          Toast.show(data.description || data.message || 'Could not add to cart', 'error');
          return Promise.reject(data);
        }
        return Cart.refresh().then(function () {
          document.dispatchEvent(new CustomEvent('cart:added', { detail: { items: data } }));
          return data;
        });
      });
    },

    change: function (payload) {
      return fetchJSON(Theme.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (cart) {
        Cart.state = cart;
        Cart.render(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        return cart;
      });
    },

    update: function (payload) {
      return fetchJSON(Theme.routes.cart_update_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (cart) {
        Cart.state = cart;
        Cart.render(cart);
        return cart;
      });
    },

    refresh: function () {
      return Promise.all([
        Cart.get(),
        fetch('?section_id=cart-drawer').then(function (r) { return r.text(); }).catch(function () { return null; })
      ]).then(function (res) {
        var cart = res[0], html = res[1];
        Cart.state = cart;
        if (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-drawer-content]');
          var target = $('[data-cart-drawer-content]');
          if (fresh && target) target.innerHTML = fresh.innerHTML;
        }
        Cart.render(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        return cart;
      });
    },

    render: function (cart) {
      // Bubble counts
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      $$('[data-cart-total]').forEach(function (el) {
        el.innerHTML = formatMoney(cart.total_price);
      });
      Cart.renderShippingBar(cart);
    },

    renderShippingBar: function (cart) {
      var bars = $$('[data-shipping-bar]');
      if (!bars.length) return;
      var threshold = parseFloat(Theme.settings.freeShippingThreshold || 0) * 100;
      if (!threshold) return;
      var remaining = threshold - cart.total_price;
      bars.forEach(function (bar) {
        var fill = $('[data-shipping-bar-fill]', bar);
        var text = $('[data-shipping-bar-text]', bar);
        var pct = Math.min(100, (cart.total_price / threshold) * 100);
        if (fill) fill.style.width = pct + '%';
        if (remaining <= 0) {
          bar.classList.add('free-shipping-bar--complete');
          if (text) text.innerHTML = Theme.strings.freeShippingUnlocked || 'Free delivery unlocked!';
        } else {
          bar.classList.remove('free-shipping-bar--complete');
          if (text) {
            var tpl = Theme.strings.freeShippingProgress || 'You are [amount] away from free delivery';
            text.innerHTML = tpl.replace('[amount]', '<strong>' + formatMoney(remaining) + '</strong>');
          }
        }
      });
    },

    openDrawer: function () {
      if (Theme.settings.cartType === 'drawer' && Theme.drawers.cart) Theme.drawers.cart.open();
      else window.location.href = Theme.routes.cart_url;
    }
  };
  Theme.Cart = Cart;

  function initCartEvents() {
    // Quantity buttons + remove links inside cart drawer / cart page
    document.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('[data-qty-btn]');
      if (qtyBtn) {
        var wrap = qtyBtn.closest('.qty');
        var input = $('input', wrap);
        var step = qtyBtn.getAttribute('data-qty-btn') === 'plus' ? 1 : -1;
        var min = parseInt(input.getAttribute('min') || '0', 10);
        var next = Math.max(min, (parseInt(input.value, 10) || 0) + step);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      var remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        var key = remove.getAttribute('data-cart-remove');
        Cart.change({ id: key, quantity: 0 }).then(function () { Cart.refresh(); });
      }
    });

    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      var key = input.getAttribute('data-cart-qty-input');
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      Cart.change({ id: key, quantity: qty }).then(function () { Cart.refresh(); });
    });

    // Cart note
    var noteEl = $('[data-cart-note]');
    if (noteEl) {
      noteEl.addEventListener('change', debounce(function () {
        Cart.update({ note: noteEl.value });
      }, 400));
    }

    // Terms checkbox gate
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-checkout-btn]');
      if (!btn) return;
      var terms = $('[data-cart-terms]');
      if (terms && !terms.checked) {
        e.preventDefault();
        Toast.show(Theme.strings.termsError || 'Please accept the terms and conditions', 'error');
      }
    });
  }

  /* ---------------- Add to cart forms ---------------- */

  function initProductForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();
      var btn = $('[data-add-to-cart]', form);
      var formData = new FormData(form);
      var items = { id: formData.get('id'), quantity: parseInt(formData.get('quantity') || '1', 10) };
      var props = {};
      formData.forEach(function (val, key) {
        var m = key.match(/^properties\[(.+)\]$/);
        if (m && val) props[m[1]] = val;
      });
      if (Object.keys(props).length) items.properties = props;
      var sellingPlan = formData.get('selling_plan');
      if (sellingPlan) items.selling_plan = sellingPlan;

      if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
      Cart.add(items)
        .then(function () {
          Toast.show(Theme.strings.addedToCart || 'Added to cart', 'success');
          Cart.openDrawer();
        })
        .catch(function () {})
        .finally(function () {
          if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
        });
    });

    // Quick add buttons (single-variant products)
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-quick-add');
      btn.classList.add('is-loading');
      btn.disabled = true;
      Cart.add({ id: id, quantity: 1 })
        .then(function () {
          Toast.show(Theme.strings.addedToCart || 'Added to cart', 'success');
          Cart.openDrawer();
        })
        .catch(function () {})
        .finally(function () { btn.classList.remove('is-loading'); btn.disabled = false; });
    });
  }

  /* ---------------- Variant picker ---------------- */

  function VariantPicker(root) {
    this.root = root;
    this.productId = root.getAttribute('data-variant-picker');
    var dataEl = $('[data-variant-json]', root) || document.querySelector('[data-variant-json="' + this.productId + '"]');
    try { this.variants = JSON.parse(dataEl.textContent); } catch (err) { this.variants = []; }
    this.form = root.closest('[data-product-form]') || document.querySelector('[data-product-form]');
    var self = this;
    root.addEventListener('change', function () { self.onChange(); });
    this.onChange(true);
  }
  VariantPicker.prototype.selectedOptions = function () {
    return $$('fieldset', this.root).map(function (fs) {
      var checked = $('input:checked', fs);
      if (checked) return checked.value;
      var sel = $('select', fs);
      return sel ? sel.value : null;
    });
  };
  VariantPicker.prototype.findVariant = function () {
    var opts = this.selectedOptions();
    return this.variants.find(function (v) {
      return opts.every(function (o, i) { return !o || v.options[i] === o; });
    });
  };
  VariantPicker.prototype.onChange = function (initial) {
    var variant = this.findVariant();
    this.updateLabels();
    this.updateAvailability();
    if (!variant) { this.setUnavailable(); return; }
    this.current = variant;

    if (!initial) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    var idInput = this.form && $('[name="id"]', this.form);
    if (idInput) idInput.value = variant.id;

    // Price (main block, COD summary, any other mirror)
    var priceHtml = '<span class="' + (variant.compare_at_price > variant.price ? 'price__sale' : 'price__regular') + '">' + formatMoney(variant.price) + '</span>';
    if (variant.compare_at_price > variant.price) {
      priceHtml += '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
      var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
      priceHtml += '<span class="price__save">-' + pct + '%</span>';
    }
    $$('[data-variant-price]').forEach(function (el) { el.innerHTML = priceHtml; });

    // Keep the COD form pointed at the selected variant, price and all
    $$('[data-cod-variant]').forEach(function (el) { el.value = variant.id; });
    $$('[data-cod-main]').forEach(function (line) {
      line.setAttribute('data-id', variant.id);
      line.setAttribute('data-price', variant.price);
      var vt = $('[data-cod-variant-title]', line);
      if (vt) vt.textContent = variant.title;
      var codModal = line.closest('[data-cod-modal]');
      if (codModal) renderCOD(codModal);
    });

    // Re-price the quantity-discount tiers off the new variant
    $$('[data-volume]').forEach(function (wrap) {
      wrap.setAttribute('data-unit-price', variant.price);
      renderVolume(wrap);
    });

    // SKU
    var skuEl = $('[data-variant-sku]');
    if (skuEl) { skuEl.textContent = variant.sku || ''; skuEl.hidden = !variant.sku; }

    // Media
    if (variant.featured_media) {
      var target = document.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
      if (target) selectMedia(variant.featured_media.id);
    }

    // Buttons
    var btns = $$('[data-add-to-cart]');
    btns.forEach(function (btn) {
      var text = $('.btn__text', btn) || btn;
      if (variant.available) {
        btn.disabled = false;
        btn.classList.remove('is-disabled');
        text.textContent = Theme.strings.addToCart || 'Add to cart';
      } else {
        btn.disabled = true;
        btn.classList.add('is-disabled');
        text.textContent = Theme.strings.soldOut || 'Sold out';
      }
    });
    var payBtn = $('.shopify-payment-button');
    if (payBtn) payBtn.hidden = !variant.available;

    // Inventory
    this.updateInventory(variant);
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: variant } }));
  };
  VariantPicker.prototype.updateLabels = function () {
    $$('fieldset', this.root).forEach(function (fs) {
      var out = $('[data-option-selected]', fs);
      var checked = $('input:checked', fs);
      if (out && checked) out.textContent = checked.value;
    });
  };
  VariantPicker.prototype.updateAvailability = function () {
    var self = this;
    var groups = $$('fieldset', this.root);
    groups.forEach(function (fs, groupIndex) {
      $$('input', fs).forEach(function (input) {
        var trial = self.selectedOptions();
        trial[groupIndex] = input.value;
        var match = self.variants.find(function (v) {
          return trial.every(function (o, i) { return i === groupIndex ? v.options[i] === o : (!o || v.options[i] === o); });
        });
        var exists = !!match;
        var available = match && match.available;
        input.disabled = !exists;
        var label = fs.querySelector('label[for="' + input.id + '"]');
        if (label) label.classList.toggle('is-unavailable', !available);
      });
    });
  };
  VariantPicker.prototype.updateInventory = function (variant) {
    var wrap = $('[data-inventory]');
    if (!wrap) return;
    var threshold = parseInt(Theme.settings.lowStockThreshold || 10, 10);
    var qty = variant.inventory_quantity;
    var policy = variant.inventory_policy;
    var management = variant.inventory_management;
    wrap.className = 'stock-indicator';
    var text = $('[data-inventory-text]', wrap);
    var bar = $('[data-inventory-bar]', wrap.parentNode || wrap);

    if (!variant.available) {
      wrap.classList.add('stock-indicator--out');
      if (text) text.textContent = Theme.strings.outOfStock || 'Out of stock';
      if (bar) bar.hidden = true;
      return;
    }
    if (management && typeof qty === 'number' && qty > 0 && qty <= threshold) {
      wrap.classList.add('stock-indicator--low');
      var tpl = Theme.strings.lowStock || 'Only [count] left in stock';
      if (text) text.textContent = tpl.replace('[count]', qty);
      if (bar) {
        bar.hidden = false;
        var fill = $('[data-inventory-bar-fill]', bar);
        if (fill) fill.style.width = Math.max(8, (qty / threshold) * 100) + '%';
      }
    } else {
      if (text) text.textContent = Theme.strings.inStock || 'In stock';
      if (bar) bar.hidden = true;
    }
  };
  VariantPicker.prototype.setUnavailable = function () {
    $$('[data-add-to-cart]').forEach(function (btn) {
      btn.disabled = true;
      var text = $('.btn__text', btn) || btn;
      text.textContent = Theme.strings.unavailable || 'Unavailable';
    });
  };

  function initVariantPickers() {
    $$('[data-variant-picker]').forEach(function (el) { new VariantPicker(el); });
  }

  /* ---------------- Product media gallery ---------------- */

  /* Two modes, one source of truth. Below 750px the CSS turns [data-media-track]
     into a scroll-snap carousel and shows every slide, so the swipe is the
     browser's; above it the slides are stacked and only .is-active is visible.
     .is-active means "the slide in view" in both, so selectMedia() stays the
     single entry point (thumbnails, variant changes, the lightbox) and only has
     to scroll the track as well when the track is scrollable — which is exactly
     the test for being in swipe mode, and avoids hard-coding the breakpoint. */

  var galleryIgnoreScrollUntil = 0;

  function trackIsSwipeable(track) {
    return !!track && track.scrollWidth - track.clientWidth > 4;
  }

  function selectMedia(mediaId, fromScroll) {
    $$('[data-media-id]').forEach(function (el) {
      el.classList.toggle('is-active', el.getAttribute('data-media-id') === String(mediaId));
    });
    $$('[data-media-thumb]').forEach(function (el) {
      var active = el.getAttribute('data-media-thumb') === String(mediaId);
      el.classList.toggle('is-active', active);
      if (active) el.setAttribute('aria-current', 'true'); else el.removeAttribute('aria-current');
    });

    if (fromScroll) return;
    var track = $('[data-media-track]');
    if (!trackIsSwipeable(track)) return;
    var slide = $('[data-media-id="' + mediaId + '"]', track);
    if (!slide) return;
    /* The smooth scroll this starts would otherwise read back as a swipe. */
    galleryIgnoreScrollUntil = Date.now() + 700;
    track.scrollLeft = slide.offsetLeft - track.offsetLeft;
  }

  function syncGalleryFromScroll(track) {
    if (Date.now() < galleryIgnoreScrollUntil) return;
    if (!trackIsSwipeable(track)) return;
    var slides = $$('[data-media-id]', track);
    if (!slides.length || !track.clientWidth) return;
    var i = Math.round(track.scrollLeft / track.clientWidth);
    if (i < 0) i = 0;
    if (i > slides.length - 1) i = slides.length - 1;
    if (slides[i].classList.contains('is-active')) return;
    selectMedia(slides[i].getAttribute('data-media-id'), true);
    keepThumbInView($('[data-media-thumb="' + slides[i].getAttribute('data-media-id') + '"]'));
  }

  /* The thumbnail strip scrolls too, so the one that just became active has to
     be brought along or the swipe loses its position indicator off-screen. */
  function keepThumbInView(thumb) {
    if (!thumb) return;
    var strip = thumb.parentNode;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    var left = thumb.offsetLeft - strip.offsetLeft;
    if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollLeft = left - (strip.clientWidth - thumb.offsetWidth) / 2;
    }
  }

  function initGalleryTracks() {
    $$('[data-media-track]').forEach(function (track) {
      if (track.getAttribute('data-track-bound')) return;
      track.setAttribute('data-track-bound', 'true');
      var t;
      track.addEventListener('scroll', function () {
        clearTimeout(t);
        t = setTimeout(function () { syncGalleryFromScroll(track); }, 90);
      }, { passive: true });
    });
  }

  function initGallery() {
    document.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-media-thumb]');
      if (thumb) { selectMedia(thumb.getAttribute('data-media-thumb')); return; }

      var zoom = e.target.closest('[data-zoom-open]');
      if (zoom) {
        var active = $('.product__media-item.is-active img');
        if (active) openLightbox(active.getAttribute('src') || active.currentSrc, active.getAttribute('alt'));
      }
    });
    initGalleryTracks();
  }

  function openLightbox(src, alt) {
    var modal = document.createElement('div');
    modal.className = 'modal modal--lightbox is-open';
    modal.innerHTML =
      '<div class="modal__overlay" data-close></div>' +
      '<div class="modal__content"><button class="modal__close" data-close aria-label="Close">' +
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
      '</button><img src="' + src + '" alt="' + (alt || '') + '"></div>';
    document.body.appendChild(modal);
    scrollLock.on();
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-close]') || e.target.classList.contains('modal__overlay')) {
        modal.remove();
        scrollLock.off();
      }
    });
  }

  /* ---------------- Quick view ---------------- */

  function initQuickView() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quick-view]');
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-quick-view');
      btn.classList.add('is-loading');
      fetch('/products/' + handle + '?section_id=quick-view')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var content = doc.querySelector('[data-quick-view-content]');
          if (!content) return;
          var modal = document.createElement('div');
          modal.className = 'modal is-open';
          modal.innerHTML =
            '<div class="modal__overlay" data-close></div>' +
            '<div class="modal__content"><button class="modal__close" data-close aria-label="Close">' +
            '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
            '</button>' + content.innerHTML + '</div>';
          document.body.appendChild(modal);
          scrollLock.on();
          trapFocus(modal);
          $$('[data-variant-picker]', modal).forEach(function (el) { new VariantPicker(el); });
          modal.addEventListener('click', function (ev) {
            if (ev.target.closest('[data-close]') || ev.target.classList.contains('modal__overlay')) {
              modal.remove();
              scrollLock.off();
            }
          });
        })
        .finally(function () { btn.classList.remove('is-loading'); });
    });
  }

  /* ---------------- Sliders ---------------- */

  function Slider(root) {
    this.root = root;
    this.track = $('[data-slider-track]', root);
    this.prev = $('[data-slider-prev]', root);
    this.next = $('[data-slider-next]', root);
    this.dotsWrap = $('[data-slider-dots]', root);
    if (!this.track) return;
    var self = this;
    if (this.prev) this.prev.addEventListener('click', function () { self.scrollBy(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.scrollBy(1); });
    this.track.addEventListener('scroll', debounce(function () { self.update(); }, 80));
    window.addEventListener('resize', debounce(function () { self.update(); }, 200));
    this.buildDots();
    this.update();
    this.initAutoplay();
  }

  /* Autoplay. The rail advances on its own, but it must never fight the person
     using it, so anything that says "I am reading this one" stops it: hover,
     keyboard focus, a touch, the tab going to the background, the section
     scrolling out of view - and a voice note playing, because sliding a clip
     out of frame halfway through a sentence is worse than not sliding at all.
     Each of those takes a hold and gives it back; the timer only runs at zero. */
  Slider.prototype.initAutoplay = function () {
    if (this.root.getAttribute('data-slider-autoplay') !== 'true') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var self = this;
    this.delay = (parseFloat(this.root.getAttribute('data-slider-speed')) || 4) * 1000;
    this.holds = 0;
    this.onScreen = false;
    this.timer = null;

    this.root.addEventListener('mouseenter', function () { self.holdAuto(); });
    this.root.addEventListener('mouseleave', function () { self.releaseAuto(); });
    this.root.addEventListener('focusin', function () { self.holdAuto(); });
    this.root.addEventListener('focusout', function () { self.releaseAuto(); });

    /* A touch means the shopper has taken the wheel. Nothing gives this hold
       back - resuming under a thumb is how a carousel loses someone's place. */
    this.track.addEventListener('touchstart', function () {
      if (!self.grabbed) { self.grabbed = true; self.holdAuto(); }
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.holdAuto('hidden');
      else self.releaseAuto('hidden');
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.onScreen = entries[0].isIntersecting;
        self.startAuto();
      }, { threshold: 0.2 }).observe(this.root);
    } else {
      this.onScreen = true;
      this.startAuto();
    }
  };
  Slider.prototype.holdAuto = function (key) {
    if (key) {
      if (this['hold_' + key]) return;
      this['hold_' + key] = true;
    }
    this.holds = (this.holds || 0) + 1;
    this.stopAuto();
  };
  Slider.prototype.releaseAuto = function (key) {
    if (key) {
      if (!this['hold_' + key]) return;
      this['hold_' + key] = false;
    }
    this.holds = Math.max(0, (this.holds || 0) - 1);
    this.startAuto();
  };
  Slider.prototype.startAuto = function () {
    if (!this.delay || this.timer || this.holds > 0 || !this.onScreen) return;
    var self = this;
    this.timer = setInterval(function () { self.autoAdvance(); }, this.delay);
  };
  Slider.prototype.stopAuto = function () {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  };
  Slider.prototype.autoAdvance = function () {
    var t = this.track;
    if (t.scrollLeft >= t.scrollWidth - t.clientWidth - 2) {
      t.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      t.scrollBy({ left: this.slideWidth(), behavior: 'smooth' });
    }
  };
  Slider.prototype.slideWidth = function () {
    var slide = $('.slider__slide', this.track);
    if (!slide) return this.track.clientWidth;
    var gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0) || 0;
    return slide.offsetWidth + gap;
  };
  Slider.prototype.scrollBy = function (dir) {
    var perView = Math.max(1, Math.round(this.track.clientWidth / this.slideWidth()));
    this.track.scrollBy({ left: dir * this.slideWidth() * Math.max(1, perView - 1 || 1), behavior: 'smooth' });
  };
  Slider.prototype.buildDots = function () {
    if (!this.dotsWrap) return;
    var slides = $$('.slider__slide', this.track);
    var perView = Math.max(1, Math.round(this.track.clientWidth / this.slideWidth()));
    var pages = Math.ceil(slides.length / perView);
    this.dotsWrap.innerHTML = '';
    /* One dot per slide on a phone, so a rail of thirty products drew a row of
       thirty dots - about 800px of them, centred in a 320px column, spilling
       off both edges. Page-level horizontal overflow makes a phone widen its
       layout viewport, and every position:fixed element (the drawers, the
       search panel, the sticky add to cart) is then measured against that
       wider box instead of the screen. Past ten pages the dots are not usable
       navigation anyway - the rail swipes and has arrows. */
    if (pages <= 1 || pages > 10) { this.dotsWrap.hidden = true; return; }
    this.dotsWrap.hidden = false;
    var self = this;
    for (var i = 0; i < pages; i++) {
      (function (idx) {
        var dot = document.createElement('button');
        dot.className = 'slider__dot';
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
        dot.addEventListener('click', function () {
          self.track.scrollTo({ left: idx * self.track.clientWidth, behavior: 'smooth' });
        });
        self.dotsWrap.appendChild(dot);
      })(i);
    }
  };
  Slider.prototype.update = function () {
    var t = this.track;
    var maxScroll = t.scrollWidth - t.clientWidth - 2;
    if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
    if (this.next) this.next.disabled = t.scrollLeft >= maxScroll;
    if (this.dotsWrap && !this.dotsWrap.hidden) {
      var idx = Math.round(t.scrollLeft / t.clientWidth);
      $$('.slider__dot', this.dotsWrap).forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
    }
  };

  /* shopify:section:load re-runs this over the whole document, not just the
     section that changed, so every existing rail would get a second Slider -
     two sets of listeners and, now, two autoplay timers running at double
     speed. The instance is parked on the element both to prevent that and so
     a voice note can reach the rail it sits in. */
  function initSliders() {
    $$('[data-slider]').forEach(function (el) {
      if (el.__slider) return;
      el.__slider = new Slider(el);
    });
  }

  /* ---------------- Review voice notes ---------------- */

  /* Customers send reviews to this shop as WhatsApp voice notes, so the rail
     has to play audio. It does it without <audio controls>, for two reasons:
     the native player looks different in every browser and is the one control
     a merchant cannot restyle, and its markup would make the browser fetch
     metadata for every clip in the rail on page load. Here the element is
     built on the first press and nothing is requested before that.

     Only one clip plays at a time - a rail where two voices talk over each
     other is a bug shoppers blame on the shop, not the browser. */

  var reviewAudioPlaying = null;

  function reviewAudioClock(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var m = Math.floor(seconds / 60);
    var sec = Math.floor(seconds % 60);
    return m + ':' + (sec < 10 ? '0' + sec : sec);
  }

  /* The bars are decorative, so progress is painted by switching a class on
     each one rather than by measuring anything. */
  function reviewAudioPaint(card, ratio) {
    var bars = $$('.review-audio__bar', card);
    var lit = Math.round(Math.min(1, Math.max(0, ratio)) * bars.length);
    bars.forEach(function (bar, i) { bar.classList.toggle('is-on', i < lit); });
  }

  /* Holds are counted on the slider, so taking one twice for the same clip
     would leave the rail stopped for good. */
  function reviewAudioHold(card, take) {
    if (take === !!card.__held) return;
    var rail = card.closest('[data-slider]');
    var slider = rail && rail.__slider;
    card.__held = take;
    if (!slider || !slider.delay) return;
    if (take) slider.holdAuto(); else slider.releaseAuto();
  }

  function reviewAudioStop(card) {
    if (card.__audio) card.__audio.pause();
    card.classList.remove('is-playing', 'is-loading');
    reviewAudioHold(card, false);
    if (reviewAudioPlaying === card) reviewAudioPlaying = null;
  }

  function reviewAudioElement(card) {
    if (card.__audio) return card.__audio;
    var src = card.getAttribute('data-audio-src');
    if (!src) return null;

    var audio = new Audio();
    audio.preload = 'none';
    audio.src = src;
    card.__audio = audio;

    var clock = $('[data-review-time]', card);

    audio.addEventListener('loadedmetadata', function () {
      if (clock) clock.textContent = reviewAudioClock(audio.duration);
    });
    audio.addEventListener('waiting', function () { card.classList.add('is-loading'); });
    audio.addEventListener('playing', function () { card.classList.remove('is-loading'); });
    audio.addEventListener('timeupdate', function () {
      if (clock) clock.textContent = reviewAudioClock(audio.currentTime);
      reviewAudioPaint(card, audio.duration ? audio.currentTime / audio.duration : 0);
    });
    audio.addEventListener('ended', function () {
      audio.currentTime = 0;
      reviewAudioPaint(card, 0);
      if (clock) clock.textContent = reviewAudioClock(audio.duration);
      reviewAudioStop(card);
    });
    audio.addEventListener('error', function () {
      if (clock) clock.textContent = Theme.strings.voiceNoteError || 'Unavailable';
      reviewAudioStop(card);
    });
    return audio;
  }

  function reviewAudioToggle(card) {
    if (!card) return;
    if (reviewAudioPlaying && reviewAudioPlaying !== card) reviewAudioStop(reviewAudioPlaying);

    var audio = reviewAudioElement(card);
    if (!audio) return;
    if (!audio.paused) { reviewAudioStop(card); return; }

    card.classList.add('is-playing');
    if (!audio.duration) card.classList.add('is-loading');
    reviewAudioHold(card, true);
    reviewAudioPlaying = card;

    var started = audio.play();
    if (started && started.catch) {
      started.catch(function () { reviewAudioStop(card); });
    }
  }

  function reviewAudioSeek(card, ratio) {
    var audio = card.__audio;
    if (!audio || !audio.duration) return;
    audio.currentTime = Math.min(1, Math.max(0, ratio)) * audio.duration;
    reviewAudioPaint(card, ratio);
  }

  function initReviewAudio() {
    /* Delegated once on the document, so clips added by the theme editor work
       without rebinding and a second call from shopify:section:load is free. */
    if (initReviewAudio.bound) return;
    initReviewAudio.bound = true;

    document.addEventListener('click', function (e) {
      var button = e.target.closest('[data-review-play]');
      if (button) {
        reviewAudioToggle(button.closest('[data-review-audio]'));
        return;
      }
      var wave = e.target.closest('[data-review-seek]');
      if (wave) {
        var box = wave.getBoundingClientRect();
        reviewAudioSeek(wave.closest('[data-review-audio]'), (e.clientX - box.left) / box.width);
      }
    });

    /* Scrubbing by clicking the bars is mouse-only, so the button carries a
       keyboard equivalent for anyone who cannot aim at a 26px strip. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var button = e.target.closest && e.target.closest('[data-review-play]');
      if (!button) return;
      var card = button.closest('[data-review-audio]');
      var audio = card && card.__audio;
      if (!audio || !audio.duration) return;
      e.preventDefault();
      audio.currentTime = Math.min(
        audio.duration,
        Math.max(0, audio.currentTime + (e.key === 'ArrowRight' ? 5 : -5))
      );
    });
  }

  /* ---------------- Hero slideshow ---------------- */

  function Slideshow(root) {
    this.root = root;
    this.slidesWrap = $('[data-slides]', root);
    this.slides = $$('[data-slide]', root);
    this.index = 0;
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.speed = parseInt(root.getAttribute('data-speed') || '5', 10) * 1000;
    this.timer = null;
    if (this.slides.length <= 1) {
      var nav = $$('[data-slide-prev], [data-slide-next], [data-slide-dots]', root);
      nav.forEach(function (n) { n.hidden = true; });
      return;
    }
    var self = this;
    var prev = $('[data-slide-prev]', root);
    var next = $('[data-slide-next]', root);
    if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); self.restart(); });
    if (next) next.addEventListener('click', function () { self.go(self.index + 1); self.restart(); });
    this.dots = $$('[data-slide-dot]', root);
    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { self.go(i); self.restart(); });
    });
    // Swipe
    var startX = 0;
    root.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 50) { self.go(self.index + (dx < 0 ? 1 : -1)); self.restart(); }
    }, { passive: true });
    root.addEventListener('mouseenter', function () { self.stop(); });
    root.addEventListener('mouseleave', function () { self.start(); });
    this.go(0);
    this.start();
    // hero-slideshow.liquid holds back every slide but the first so they do
    // not compete with it for bandwidth; fetch them once the page has loaded.
    if (document.readyState === 'complete') this.loadDeferred();
    else window.addEventListener('load', function () { self.loadDeferred(); });
  }
  Slideshow.prototype.loadDeferred = function () {
    if (this.deferredLoaded) return;
    this.deferredLoaded = true;
    $$('[data-srcset]', this.root).forEach(function (el) {
      el.setAttribute('srcset', el.getAttribute('data-srcset'));
      el.removeAttribute('data-srcset');
    });
    $$('[data-src]', this.root).forEach(function (el) {
      el.setAttribute('src', el.getAttribute('data-src'));
      el.removeAttribute('data-src');
    });
  };
  Slideshow.prototype.go = function (i) {
    var len = this.slides.length;
    this.index = (i + len) % len;
    if (this.index !== 0) this.loadDeferred();
    if (this.slidesWrap) this.slidesWrap.style.transform = 'translateX(-' + this.index * 100 + '%)';
    (this.dots || []).forEach(function (d, di) { d.classList.toggle('is-active', di === this.index); }, this);
    this.slides.forEach(function (s, si) { s.setAttribute('aria-hidden', si !== this.index); }, this);
  };
  Slideshow.prototype.start = function () {
    if (!this.autoplay) return;
    var self = this;
    this.stop();
    this.timer = setInterval(function () { self.go(self.index + 1); }, this.speed);
  };
  Slideshow.prototype.stop = function () { if (this.timer) clearInterval(this.timer); };
  Slideshow.prototype.restart = function () { this.stop(); this.start(); };

  function initSlideshows() {
    $$('[data-slideshow]').forEach(function (el) { new Slideshow(el); });
  }

  /* ---------------- Announcement rotator ---------------- */

  function initAnnouncement() {
    $$('[data-announcement]').forEach(function (root) {
      var slides = $$('.announcement__slide', root);
      if (slides.length <= 1) return;
      var i = 0;
      var speed = parseInt(root.getAttribute('data-speed') || '5', 10) * 1000;
      function go(n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, si) { s.classList.toggle('is-active', si === i); });
      }
      var prev = $('[data-ann-prev]', root);
      var next = $('[data-ann-next]', root);
      if (prev) prev.addEventListener('click', function () { go(i - 1); });
      if (next) next.addEventListener('click', function () { go(i + 1); });
      if (root.getAttribute('data-autoplay') === 'true') {
        setInterval(function () { go(i + 1); }, speed);
      }
    });
  }

  /* ---------------- Predictive search ---------------- */

  function initSearch() {
    var modal = $('[data-search-modal]');
    if (!modal) return;
    var input = $('[data-search-input]', modal);
    var results = $('[data-search-results]', modal);

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-open]')) {
        e.preventDefault();
        modal.classList.add('is-open');
        scrollLock.on();
        setTimeout(function () { if (input) input.focus(); }, 100);
      }
      if (e.target.closest('[data-search-close]') || e.target === $('.search-modal__overlay', modal)) {
        modal.classList.remove('is-open');
        scrollLock.off();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) {
        modal.classList.remove('is-open');
        scrollLock.off();
      }
    });

    if (!input || !results || !Theme.settings.predictiveSearch) return;

    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      var types = ['product', 'collection'];
      if (Theme.settings.searchArticles) types.push('article');
      if (Theme.settings.searchPages) types.push('page');
      var url = Theme.routes.predictive_search_url +
        '?q=' + encodeURIComponent(q) +
        '&resources[type]=' + types.join(',') +
        '&resources[limit]=6&section_id=predictive-search';
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-predictive-results]');
          results.innerHTML = fresh ? fresh.innerHTML : '';
        })
        .catch(function () {});
    }, 280);
    input.addEventListener('input', run);
  }

  /* ---------------- Sticky header ---------------- */

  function initStickyHeader() {
    var header = $('[data-header]');
    if (!header) return;
    var lastY = 0;
    window.addEventListener('scroll', function () {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      lastY = y;
    }, { passive: true });
  }

  /* ---------------- Sticky ATC ---------------- */

  function initStickyATC() {
    var bar = $('[data-sticky-atc]');
    if (!bar) return;
    var anchor = $('[data-atc-anchor]');
    if (!anchor) return;
    document.body.classList.add('has-sticky-atc');
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { rootMargin: '0px 0px 0px 0px' });
    observer.observe(anchor);

    // Mirror variant selection
    document.addEventListener('variant:changed', function (e) {
      var idInput = $('[name="id"]', bar);
      if (idInput) idInput.value = e.detail.variant.id;
      var priceEl = $('[data-sticky-price]', bar);
      if (priceEl) priceEl.innerHTML = formatMoney(e.detail.variant.price);
      var btn = $('[data-add-to-cart]', bar);
      if (btn) btn.disabled = !e.detail.variant.available;
    });
  }

  /* ---------------- Tabs ---------------- */

  function initTabs() {
    $$('[data-tabs]').forEach(function (root) {
      var btns = $$('[data-tab-btn]', root);
      var panels = $$('[data-tab-panel]', root);
      btns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.getAttribute('data-tab-btn');
          btns.forEach(function (b) {
            var active = b === btn;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-selected', active);
          });
          panels.forEach(function (p) {
            p.classList.toggle('is-active', p.getAttribute('data-tab-panel') === target);
          });
        });
      });
    });
  }

  /* ---------------- Live viewers ---------------- */

  // Presentational only - nothing is measured. It used to be derived from the
  // product id in Liquid, so the same page showed the same "live" number on
  // every visit, which reads as fake the second anyone looks twice. Re-roll
  // inside the merchant's range per page view, then drift by one now and then
  // so a number sitting on screen does not stay frozen.
  function initViewers() {
    $$('[data-viewers]').forEach(function (root) {
      var min = parseInt(root.getAttribute('data-viewers-min'), 10);
      var max = parseInt(root.getAttribute('data-viewers-max'), 10);
      if (isNaN(min)) min = 12;
      if (isNaN(max) || max <= min) max = min + 10;

      var label = root.querySelector('[data-viewers-text]');
      var template = (Theme.strings && Theme.strings.viewers) || '';
      if (!label || template.indexOf('[count]') === -1) return;

      var count = min + Math.floor(Math.random() * (max - min + 1));
      var render = function () { label.textContent = template.replace('[count]', count); };
      render();

      if (root.dataset.viewersTimer) clearInterval(root.dataset.viewersTimer);
      var timer = setInterval(function () {
        if (!document.body.contains(root)) { clearInterval(timer); return; }
        // A step of +/-1 looks like someone arriving or leaving; bigger jumps do not.
        var next = count + (Math.random() < 0.5 ? -1 : 1);
        if (next < min || next > max) next = count - (next - count);
        count = next;
        render();
      }, 9000 + Math.floor(Math.random() * 6000));
      root.dataset.viewersTimer = timer;
    });
  }

  /* ---------------- Countdown ---------------- */

  function initCountdowns() {
    $$('[data-countdown]').forEach(function (root) {
      var end = new Date(root.getAttribute('data-countdown')).getTime();
      if (isNaN(end)) return;
      var evergreen = root.getAttribute('data-evergreen');
      if (evergreen) {
        var hours = parseFloat(evergreen);
        var key = 'gt_countdown_' + (root.id || 'default');
        var stored = localStorage.getItem(key);
        if (!stored || parseInt(stored, 10) < Date.now()) {
          stored = String(Date.now() + hours * 3600 * 1000);
          localStorage.setItem(key, stored);
        }
        end = parseInt(stored, 10);
      }
      var els = {
        days: $('[data-cd-days]', root),
        hours: $('[data-cd-hours]', root),
        minutes: $('[data-cd-minutes]', root),
        seconds: $('[data-cd-seconds]', root)
      };
      function pad(n) { return n < 10 ? '0' + n : String(n); }
      function tick() {
        var diff = end - Date.now();
        if (diff <= 0) {
          diff = 0;
          if (root.getAttribute('data-hide-on-end') === 'true') {
            var section = root.closest('.shopify-section') || root;
            section.hidden = true;
          }
          clearInterval(timer);
        }
        var d = Math.floor(diff / 86400000);
        var h = Math.floor((diff % 86400000) / 3600000);
        var m = Math.floor((diff % 3600000) / 60000);
        var s = Math.floor((diff % 60000) / 1000);
        if (els.days) els.days.textContent = pad(d);
        if (els.hours) els.hours.textContent = pad(h);
        if (els.minutes) els.minutes.textContent = pad(m);
        if (els.seconds) els.seconds.textContent = pad(s);
      }
      tick();
      var timer = setInterval(tick, 1000);
    });
  }

  /* ---------------- Scroll animations ---------------- */

  function initAnimations() {
    if (!Theme.settings.animations) return;
    if (!('IntersectionObserver' in window)) {
      $$('.animate').forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0.05 });
    $$('.animate').forEach(function (el, i) {
      el.style.transitionDelay = Math.min(i % 6, 5) * 60 + 'ms';
      io.observe(el);
    });
  }

  /* ---------------- Facets (collection filtering) ---------------- */

  function initFacets() {
    var form = $('[data-facet-form]');
    if (!form) return;

    function renderFrom(url) {
      var container = $('[data-collection-results]');
      if (container) container.style.opacity = '.45';
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          ['[data-collection-results]', '[data-facet-form]', '[data-active-filters]', '[data-collection-count]'].forEach(function (sel) {
            var fresh = doc.querySelector(sel);
            var cur = document.querySelector(sel);
            if (fresh && cur) cur.innerHTML = fresh.innerHTML;
          });
          window.history.pushState({}, '', url);
          if (container) container.style.opacity = '';
          initAnimations();
        })
        .catch(function () { if (container) container.style.opacity = ''; });
    }

    document.addEventListener('change', function (e) {
      if (!e.target.closest('[data-facet-form]')) return;
      var f = $('[data-facet-form]');
      var params = new URLSearchParams(new FormData(f));
      renderFrom(window.location.pathname + '?' + params.toString());
    });

    document.addEventListener('click', function (e) {
      var clear = e.target.closest('[data-facet-clear]');
      if (clear) {
        e.preventDefault();
        renderFrom(clear.getAttribute('href'));
      }
      var toggle = e.target.closest('[data-facet-toggle]');
      if (toggle) {
        var panel = $('[data-facets]');
        if (panel) { panel.classList.toggle('is-open'); scrollLock.on(); }
      }
      var closeF = e.target.closest('[data-facet-close]');
      if (closeF) {
        var p = $('[data-facets]');
        if (p) { p.classList.remove('is-open'); scrollLock.off(); }
      }
      var pageLink = e.target.closest('[data-collection-results] .pagination__item');
      if (pageLink && pageLink.getAttribute('href')) {
        e.preventDefault();
        renderFrom(pageLink.getAttribute('href'));
        window.scrollTo({ top: $('[data-collection-results]').offsetTop - 120, behavior: 'smooth' });
      }
    });

    var sort = $('[data-sort-by]');
    if (sort) {
      sort.addEventListener('change', function () {
        var params = new URLSearchParams(window.location.search);
        params.set('sort_by', sort.value);
        renderFrom(window.location.pathname + '?' + params.toString());
      });
    }
  }

  /* ---------------- Quantity discounts ---------------- */

  function renderVolume(wrap) {
    var unit = parseInt(wrap.getAttribute('data-unit-price'), 10) || 0;
    $$('.vol__tier', wrap).forEach(function (tier) {
      var input = $('input', tier);
      if (!input) return;
      var qty = parseInt(input.getAttribute('data-vol-qty'), 10) || 1;
      var off = parseInt(input.getAttribute('data-vol-off'), 10) || 0;
      var full = unit * qty;
      var now = Math.round(full * (100 - off) / 100);

      var nowEl = $('[data-vol-now]', tier);
      var wasEl = $('[data-vol-was]', tier);
      if (nowEl) nowEl.innerHTML = formatMoney(now);
      if (wasEl) wasEl.innerHTML = formatMoney(full);
      tier.classList.toggle('is-active', input.checked);
    });
  }

  function initVolume() {
    $$('[data-volume]').forEach(function (wrap) {
      renderVolume(wrap);
      wrap.addEventListener('change', function (e) {
        var input = e.target.closest('input[data-vol-qty]');
        if (!input) return;
        // Picking a tier just sets the quantity; Shopify's automatic discount
        // is what actually takes the money off.
        var form = wrap.closest('form');
        var qtyField = form && $('[name="quantity"]', form);
        if (qtyField) {
          qtyField.value = input.getAttribute('data-vol-qty');
          qtyField.dispatchEvent(new Event('change', { bubbles: true }));
        }
        renderVolume(wrap);
      });
    });
  }

  /* ---------------- COD order form ---------------- */

  /* ---------------- Cash on delivery ---------------- */

  function codMoney(cents) { return formatMoney(cents, Theme.settings.moneyFormat); }

  /* Recalculate the little order sitting inside the popup. */
  function renderCOD(modal) {
    var main = $('[data-cod-main]', modal);
    if (!main) return;

    var qtyInput = $('[data-cod-qty-input]', modal);
    var qty = Math.max(1, parseInt(qtyInput ? qtyInput.value : '1', 10) || 1);
    if (qtyInput) qtyInput.value = qty;

    var unit = parseInt(main.getAttribute('data-price'), 10) || 0;
    var subtotal = unit * qty;
    var lines = [main.getAttribute('data-title') + ' x ' + qty];
    var items = [{ id: main.getAttribute('data-id'), quantity: qty }];

    $$('[data-cod-addon]', modal).forEach(function (box) {
      var label = box.closest('.cod-addon');
      if (label) label.classList.toggle('is-on', box.checked);
      if (!box.checked) return;
      subtotal += parseInt(box.getAttribute('data-price'), 10) || 0;
      lines.push(box.getAttribute('data-title') + ' x 1');
      items.push({ id: box.getAttribute('data-id'), quantity: 1 });
    });

    var fee = parseInt(modal.getAttribute('data-cod-ship'), 10) || 0;
    var freeOver = parseInt(modal.getAttribute('data-cod-free-over'), 10) || 0;
    var free = freeOver > 0 && subtotal >= freeOver;
    var shipping = free ? 0 : fee;

    var linePrice = $('[data-cod-line-price]', modal);
    if (linePrice) linePrice.textContent = codMoney(unit * qty);

    var sub = $('[data-cod-subtotal]', modal);
    if (sub) sub.textContent = codMoney(subtotal);

    var ship = $('[data-cod-shipping]', modal);
    if (ship) {
      ship.textContent = shipping === 0
        ? (modal.getAttribute('data-cod-free-label') || 'FREE')
        : codMoney(shipping);
      ship.classList.toggle('is-free', shipping === 0);
    }

    var total = $('[data-cod-total]', modal);
    if (total) total.textContent = codMoney(subtotal + shipping);

    // Only nudge towards free delivery while it is still out of reach.
    var note = $('[data-cod-ship-note]', modal);
    if (note) note.hidden = !(freeOver > 0 && !free);

    modal.codOrder = {
      items: items,
      lines: lines,
      subtotal: subtotal,
      shipping: shipping,
      total: subtotal + shipping
    };
  }

  function initCODForm() {
    /* Open / close the popup that holds the form. */
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-cod-open]');
      if (opener) {
        var modal = document.getElementById(opener.getAttribute('data-cod-open'));
        if (modal) {
          modal.classList.add('is-open');
          document.body.classList.add('no-scroll');
          renderCOD(modal);
          trapFocus(modal);
          var first = $('input:not([type="hidden"]):not(.visually-hidden)', modal);
          if (first) first.focus();
        }
        return;
      }
      var closer = e.target.closest('[data-cod-close]');
      if (closer) {
        var open = closer.closest('[data-cod-modal]');
        if (open) { open.classList.remove('is-open'); document.body.classList.remove('no-scroll'); }
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      $$('[data-cod-modal].is-open').forEach(function (m) {
        m.classList.remove('is-open');
        document.body.classList.remove('no-scroll');
      });
    });

    $$('[data-cod-modal]').forEach(function (modal) {
      /* Quantity and extras: anything that moves the total. */
      modal.addEventListener('click', function (e) {
        var step = e.target.closest('[data-cod-qty]');
        if (!step) return;
        var input = $('[data-cod-qty-input]', modal);
        if (!input) return;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(step.getAttribute('data-cod-qty'), 10));
        renderCOD(modal);
      });
      modal.addEventListener('change', function (e) {
        if (e.target.closest('[data-cod-addon]') || e.target.closest('[data-cod-qty-input]')) renderCOD(modal);
      });
      modal.addEventListener('input', function (e) {
        if (e.target.closest('[data-cod-qty-input]')) renderCOD(modal);
      });
      renderCOD(modal);
    });

    $$('[data-cod-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var modal = form.closest('[data-cod-modal]');
        var btn = $('[data-cod-submit]', form);
        var valid = true;

        $$('[required]', form).forEach(function (field) {
          var wrap = field.closest('.field');
          var ok = field.value.trim().length > 0;
          if (field.type === 'tel') ok = ok && field.value.replace(/\D/g, '').length >= 10;
          if (wrap) wrap.classList.toggle('field--error', !ok);
          if (!ok) valid = false;
        });
        if (!valid) { Toast.show(Theme.strings.codInvalid || 'Please fill all required fields', 'error'); return; }

        renderCOD(modal);
        var order = modal.codOrder || { items: [], lines: [], shipping: 0, total: 0 };

        function val(name) {
          var el = $('[name="' + name + '"]', form);
          return el ? el.value.trim() : '';
        }
        var name = val('cod_name');
        var phone = val('cod_phone');
        var address = val('cod_address');
        var city = val('cod_city');

        var attributes = {
          'Order Type': 'Cash on Delivery',
          'COD Name': name,
          'COD Phone': phone,
          'COD Address': address,
          'COD City': city,
          'COD Items': order.lines.join(', '),
          'COD Delivery charge': codMoney(order.shipping),
          'COD Total': codMoney(order.total)
        };

        if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }

        Cart.add(order.items)
          .then(function () { return Cart.update({ attributes: attributes }); })
          .then(function () {
            if (modal.getAttribute('data-cod-mode') === 'cart') {
              // Stay put. The merchant rings back from the cart attributes.
              var done = $('[data-cod-done]', modal);
              var summary = $('[data-cod-done-summary]', modal);
              if (summary) {
                summary.innerHTML = '<p>' + order.lines.join('<br>') + '</p>' +
                  '<p><strong>' + codMoney(order.total) + '</strong></p>';
              }
              form.hidden = true;
              if (done) done.hidden = false;
              if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
              return;
            }

            // Hand checkout everything we already asked for, so the shopper is
            // one tap from done instead of retyping their address.
            var parts = name.split(' ');
            var q = [
              'checkout[shipping_address][first_name]=' + encodeURIComponent(parts[0] || ''),
              'checkout[shipping_address][last_name]=' + encodeURIComponent(parts.slice(1).join(' ')),
              'checkout[shipping_address][address1]=' + encodeURIComponent(address),
              'checkout[shipping_address][city]=' + encodeURIComponent(city),
              'checkout[shipping_address][phone]=' + encodeURIComponent(phone)
            ].join('&');
            window.location.href = '/checkout?' + q;
          })
          .catch(function () {
            if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
          });
      });
    });
  }

  /* ---------------- Wishlist (localStorage) ---------------- */

  var Wishlist = {
    key: 'gt_wishlist',
    get: function () {
      try { return JSON.parse(localStorage.getItem(this.key) || '[]'); } catch (e) { return []; }
    },
    set: function (list) {
      try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (e) {}
      this.render();
    },
    toggle: function (handle) {
      var list = this.get();
      var i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1); else list.push(handle);
      this.set(list);
      return i === -1;
    },
    render: function () {
      var list = this.get();
      $$('[data-wishlist-toggle]').forEach(function (btn) {
        btn.classList.toggle('is-active', list.indexOf(btn.getAttribute('data-wishlist-toggle')) > -1);
      });
      $$('[data-wishlist-count]').forEach(function (el) {
        el.textContent = list.length;
        el.hidden = list.length === 0;
      });
    }
  };
  Theme.Wishlist = Wishlist;

  function initWishlist() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      var added = Wishlist.toggle(btn.getAttribute('data-wishlist-toggle'));
      Toast.show(added ? (Theme.strings.wishlistAdded || 'Added to wishlist') : (Theme.strings.wishlistRemoved || 'Removed from wishlist'), 'success', 1800);
    });
    Wishlist.render();
  }

  /* ---------------- Recently viewed ---------------- */

  function initRecentlyViewed() {
    var key = 'gt_recently_viewed';
    var current = Theme.settings.currentProductHandle;
    var list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) {}

    if (current) {
      list = list.filter(function (h) { return h !== current; });
      list.unshift(current);
      list = list.slice(0, 12);
      try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) {}
    }

    var container = $('[data-recently-viewed]');
    if (!container) return;
    var handles = list.filter(function (h) { return h !== current; }).slice(0, 8);
    if (!handles.length) { var s = container.closest('.shopify-section'); if (s) s.hidden = true; return; }
    var query = handles.map(function (h) { return 'handle:' + h; }).join(' OR ');
    fetch('/search?type=product&options[unavailable_products]=last&q=' + encodeURIComponent(query) + '&section_id=recently-viewed')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-recently-viewed-inner]');
        if (fresh && fresh.children.length) {
          container.innerHTML = fresh.innerHTML;
          initSliders();
        } else {
          var sec = container.closest('.shopify-section');
          if (sec) sec.hidden = true;
        }
      })
      .catch(function () {});
  }

  /* ---------------- Video reviews ---------------- */

  function initVideoCards() {
    $$('[data-vt-modal]').forEach(function (modal) {
      if (modal.dataset.ready) return;
      modal.dataset.ready = '1';

      var scope = modal.closest('.shopify-section') || document;
      var cards = $$('[data-video-card][data-vt-src]', scope);
      if (!cards.length) return;

      var video = $('[data-vt-video]', modal);
      var bar = $('[data-vt-bar]', modal);
      var track = $('[data-vt-progress]', modal);
      var list = $('[data-vt-list]', modal);
      var empty = $('[data-vt-empty]', modal);
      var playBtn = $('[data-vt-play]', modal);
      var muteBtn = $('[data-vt-mute]', modal);
      var idx = 0;

      // A single clip needs no arrows.
      if (cards.length < 2) {
        $$('[data-vt-prev], [data-vt-next]', modal).forEach(function (b) { b.hidden = true; });
      }

      function syncPlay() { modal.classList.toggle('is-paused', video.paused); }
      function syncMute() {
        modal.classList.toggle('is-muted', video.muted);
        muteBtn.setAttribute('aria-pressed', String(!video.muted));
      }

      function show(i) {
        idx = (i + cards.length) % cards.length;
        var card = cards[idx];
        var poster = card.getAttribute('data-vt-poster');

        video.pause();
        video.src = card.getAttribute('data-vt-src');
        if (poster) video.poster = poster; else video.removeAttribute('poster');
        video.currentTime = 0;
        bar.style.width = '0%';

        var tpl = $('template[data-vt-products]', card.parentNode);
        var html = tpl ? tpl.innerHTML.trim() : '';
        list.innerHTML = html;
        empty.hidden = html !== '';

        // Sound is the point of opening the popup, and the click that got us
        // here counts as the gesture browsers ask for. Fall back to muted
        // playback rather than a frozen frame if it is refused anyway.
        video.muted = false;
        var started = video.play();
        if (started && started.catch) {
          started.catch(function () {
            video.muted = true;
            syncMute();
            video.play().catch(function () {});
          });
        }
        syncMute();
      }

      function open(i) {
        modal.classList.add('is-open');
        scrollLock.on();
        trapFocus(modal);
        show(i);
      }

      function close() {
        video.pause();
        video.removeAttribute('src');
        video.load();
        modal.classList.remove('is-open');
        scrollLock.off();
      }

      cards.forEach(function (card, i) {
        card.addEventListener('click', function (e) {
          if (e.target.closest('.video-card__product')) return;
          e.preventDefault();
          open(i);
        });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(i); }
        });
      });

      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-vt-close]') || e.target.classList.contains('modal__overlay')) {
          close();
          return;
        }
        if (e.target.closest('[data-vt-prev]')) { show(idx - 1); return; }
        if (e.target.closest('[data-vt-next]')) { show(idx + 1); return; }
        if (e.target.closest('[data-vt-play]')) {
          if (video.paused) video.play(); else video.pause();
          return;
        }
        if (e.target.closest('[data-vt-mute]')) {
          video.muted = !video.muted;
          syncMute();
          return;
        }
        // The shared quick-add handler does the work; step out of its way.
        if (e.target.closest('[data-quick-add]')) close();
      });

      track.addEventListener('click', function (e) {
        if (!video.duration) return;
        var rect = track.getBoundingClientRect();
        video.currentTime = ((e.clientX - rect.left) / rect.width) * video.duration;
      });

      video.addEventListener('timeupdate', function () {
        if (!video.duration) return;
        bar.style.width = (video.currentTime / video.duration) * 100 + '%';
      });
      video.addEventListener('ended', function () { show(idx + 1); });
      video.addEventListener('play', syncPlay);
      video.addEventListener('pause', syncPlay);
      video.addEventListener('click', function () {
        if (video.paused) video.play(); else video.pause();
      });

      document.addEventListener('keydown', function (e) {
        if (!modal.classList.contains('is-open')) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowRight') show(idx + 1);
        else if (e.key === 'ArrowLeft') show(idx - 1);
      });
    });
  }

  /* ---------------- Marquee ---------------- */

  /* ---------------- Lazy <video> sources ---------------- */

  /* <img loading="lazy"> has no equivalent for video, so the markup ships the
     file in data-src and we attach it when the tile gets close. Without this
     every <video preload="metadata"> on the page opens a connection during the
     first paint, however far down the page it sits. */
  function initLazyVideos() {
    var vids = $$('video[data-lazy-video]');
    if (!vids.length) return;

    function load(video) {
      if (video.dataset.lazyLoaded) return;
      video.dataset.lazyLoaded = 'true';
      $$('source[data-src]', video).forEach(function (source) {
        source.src = source.getAttribute('data-src');
        source.removeAttribute('data-src');
      });
      video.load();
    }

    if (!('IntersectionObserver' in window)) { vids.forEach(load); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        load(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '400px 0px' });

    vids.forEach(function (v) { io.observe(v); });
  }

  function initMarquee() {
    $$('[data-marquee]').forEach(function (root) {
      var track = $('.marquee__track', root);
      if (!track || track.dataset.cloned) return;
      track.innerHTML += track.innerHTML;
      track.dataset.cloned = 'true';
    });
  }

  /* ---------------- Share ---------------- */

  function initShare() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-share-copy]');
      if (!btn) return;
      e.preventDefault();
      var url = btn.getAttribute('data-share-copy') || window.location.href;
      if (navigator.share && window.matchMedia('(max-width: 749px)').matches) {
        navigator.share({ url: url, title: document.title }).catch(function () {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(function () {
          Toast.show(Theme.strings.linkCopied || 'Link copied', 'success', 1800);
        });
      }
    });
  }

  /* ---------------- Newsletter ---------------- */

  function initNewsletterFocus() {
    if (window.location.search.indexOf('customer_posted=true') === -1) return;
    var msg = $('[data-newsletter-success]');
    if (msg) msg.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /* ---------------- Bundle builder ---------------- */

  function initBundleBuilder() {
    $$('[data-bundle]').forEach(function (root) {
      var checkboxes = $$('input[type="checkbox"][data-bundle-item]', root);
      var totalEl = $('[data-bundle-total]', root);
      var btn = $('[data-bundle-add]', root);

      function update() {
        var total = 0;
        checkboxes.forEach(function (cb) {
          if (cb.checked) total += parseInt(cb.getAttribute('data-price'), 10) || 0;
        });
        if (totalEl) totalEl.innerHTML = formatMoney(total);
      }
      checkboxes.forEach(function (cb) { cb.addEventListener('change', update); });
      update();

      if (btn) {
        btn.addEventListener('click', function () {
          var items = checkboxes.filter(function (cb) { return cb.checked; })
            .map(function (cb) { return { id: cb.value, quantity: 1 }; });
          if (!items.length) { Toast.show('Select at least one product', 'error'); return; }
          btn.classList.add('is-loading');
          btn.disabled = true;
          Cart.add(items)
            .then(function () { Toast.show(Theme.strings.addedToCart || 'Added to cart', 'success'); Cart.openDrawer(); })
            .catch(function () {})
            .finally(function () { btn.classList.remove('is-loading'); btn.disabled = false; });
        });
      }
    });
  }

  /* ---------------- Card swatch preview ---------------- */

  function initCardSwatches() {
    document.addEventListener('click', function (e) {
      var sw = e.target.closest('[data-card-swatch]');
      if (!sw) return;
      e.preventDefault();
      var card = sw.closest('.card');
      var img = $('.card__image--main', card);
      var newSrc = sw.getAttribute('data-swatch-image');
      var url = sw.getAttribute('data-swatch-url');
      $$('[data-card-swatch]', card).forEach(function (s) { s.classList.remove('is-active'); });
      sw.classList.add('is-active');
      if (img && newSrc) img.src = newSrc;
      var link = $('.card__title a', card);
      if (link && url) link.href = url;
      var qa = $('[data-quick-add]', card);
      var vid = sw.getAttribute('data-swatch-variant');
      if (qa && vid) qa.setAttribute('data-quick-add', vid);
    });
  }

  /* ---------------- Product recommendations ---------------- */

  function initRecommendations() {
    $$('[data-product-recommendations]').forEach(function (root) {
      if (root.dataset.loaded) return;
      var url = root.getAttribute('data-url');
      if (!url) return;
      root.dataset.loaded = 'true';
      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-product-recommendations]');
          if (fresh && fresh.querySelector('.card')) {
            root.innerHTML = fresh.innerHTML;
            initSliders();
            initAnimations();
          } else {
            root.hidden = true;
          }
        })
        .catch(function () { root.hidden = true; });
    });
  }

  /* ---------------- Before / after comparison ---------------- */

  function initBeforeAfter() {
    $$('[data-before-after]').forEach(function (root) {
      if (root.dataset.ready) return;
      root.dataset.ready = 'true';
      var before = $('[data-ba-before]', root);
      var handle = $('[data-ba-handle]', root);
      var range = $('[data-ba-range]', root);
      if (!before || !handle || !range) return;

      function apply(pct) {
        pct = Math.max(0, Math.min(100, pct));
        // Clip rather than resize. The before layer stays the full size of the
        // card, so both photos line up pixel for pixel at any handle position.
        before.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
        handle.style.left = pct + '%';
      }
      range.addEventListener('input', function () { apply(parseFloat(range.value)); });

      // Drag anywhere on the image, not just the native range thumb.
      function fromEvent(e) {
        var rect = root.getBoundingClientRect();
        var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        return (x / rect.width) * 100;
      }
      var dragging = false;
      root.addEventListener('mousedown', function (e) { dragging = true; var p = fromEvent(e); range.value = p; apply(p); });
      window.addEventListener('mousemove', function (e) { if (dragging) { var p = fromEvent(e); range.value = p; apply(p); } });
      window.addEventListener('mouseup', function () { dragging = false; });
      root.addEventListener('touchstart', function (e) { var p = fromEvent(e); range.value = p; apply(p); }, { passive: true });
      root.addEventListener('touchmove', function (e) { var p = fromEvent(e); range.value = p; apply(p); }, { passive: true });

      apply(parseFloat(range.value));
    });
  }

  /* ---------------- Product finder quiz ---------------- */

  function initQuiz() {
    $$('[data-quiz]').forEach(function (root) {
      if (root.dataset.ready) return;
      root.dataset.ready = 'true';
      var steps = $$('[data-quiz-step]', root);
      var result = $('[data-quiz-result]', root);
      var progress = $('[data-quiz-progress]', root);
      var cta = $('[data-quiz-cta]', root);
      if (!steps.length) return;

      var index = 0;
      var lastTarget = cta ? cta.getAttribute('href') : '';

      function show(i) {
        index = i;
        steps.forEach(function (s, si) { s.classList.toggle('is-active', si === i && i < steps.length); });
        if (result) result.classList.toggle('is-active', i >= steps.length);
        if (progress) {
          var pct = Math.min(100, (i / steps.length) * 100);
          progress.style.width = (i >= steps.length ? 100 : pct) + '%';
        }
      }

      root.addEventListener('click', function (e) {
        var answer = e.target.closest('[data-quiz-answer]');
        if (answer) {
          var target = answer.getAttribute('data-quiz-target');
          if (target) lastTarget = target;
          if (cta && lastTarget) cta.setAttribute('href', lastTarget);
          show(index + 1);
          root.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return;
        }
        if (e.target.closest('[data-quiz-back]')) { show(Math.max(0, index - 1)); return; }
        if (e.target.closest('[data-quiz-restart]')) { show(0); }
      });

      show(0);
    });
  }

  /* ---------------- Footer accordions (mobile only) ---------------- */

  function initFooterAccordions() {
    var cols = $$('[data-footer-col]');
    if (!cols.length) return;
    var mq = window.matchMedia('(max-width: 749px)');
    function sync() {
      cols.forEach(function (col) {
        if (mq.matches) {
          if (!col.dataset.userToggled) col.removeAttribute('open');
        } else {
          col.setAttribute('open', '');
        }
      });
    }
    cols.forEach(function (col) {
      col.addEventListener('toggle', function () {
        if (mq.matches) col.dataset.userToggled = 'true';
      });
    });
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);
  }

  /* ---------------- Mobile bottom bar ---------------- */

  function initMobileNav() {
    var nav = $('[data-mobile-nav]');
    if (!nav) return;

    // Tell the layout to reserve space and lift the other fixed elements.
    document.body.classList.add('has-mobile-nav');
    if ($('a[href*="wa.me"]', nav)) document.body.classList.add('nav-has-whatsapp');
    if (nav.classList.contains('mnav--floating') || nav.classList.contains('mnav--glass')) {
      document.body.classList.add('nav-floating');
    }

    if (nav.getAttribute('data-hide-on-scroll') !== 'true') return;
    var lastY = window.scrollY;
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var y = window.scrollY;
        if (y > lastY + 8 && y > 140) nav.classList.add('is-hidden');
        else if (y < lastY - 8) nav.classList.remove('is-hidden');
        lastY = y;
        ticking = false;
      });
    }, { passive: true });
  }

  /* ---------------- WhatsApp assistant ---------------- */

  /*
    A chat-shaped helper on the floating WhatsApp bubble: it asks the questions
    the merchant configured, then either links to a product or hands the
    shopper to WhatsApp with their answers already written into the message.

    Nothing here writes user-facing text. The answer bubbles echo the shopper's
    own words, so no string needs a locale entry.
  */

  /* ---- The water drop ----

     Synthesised rather than shipped as an audio file, so the popup costs no
     extra request. Three details make it read as a droplet instead of a beep:

     1. The pitch RISES. A real drop traps an air bubble that shrinks while it
        oscillates, so its resonance climbs - that is the "plink". Sweeping the
        pitch downwards, as the first version did, sounds like a cartoon boop.
     2. There is a 6 ms noise transient at the front. Without that contact
        sound the tone has no attack and disappears under room noise.
     3. A short damped slap-back follows it. A drip in a dead room is easy to
        miss; the reflection is what makes it carry and what makes it read as
        water rather than a synth tone.

     Browsers refuse to start audio before the visitor has tapped or typed
     something, so a request that arrives too early is queued rather than
     thrown away, and the first gesture anywhere on the page flushes it.
  */

  var waCtx = null;
  var waQueued = null;
  var waArmed = false;
  var waEscBound = false;

  function waContext() {
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!waCtx) {
      try { waCtx = new Ctx(); } catch (e) { return null; }
    }
    return waCtx;
  }

  function waDrop(ctx, volume) {
    var t0 = ctx.currentTime + 0.02; // a hair of lead so the attack is not clipped

    var master = ctx.createGain();
    master.gain.value = volume;
    master.connect(ctx.destination);

    // Damped slap-back. Feedback is low and the loop is filtered, so it reads
    // as a tiled room rather than an effect.
    var echo = ctx.createDelay(0.5);
    echo.delayTime.value = 0.115;
    var damp = ctx.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 2400;
    var feedback = ctx.createGain();
    feedback.gain.value = 0.3;
    var wet = ctx.createGain();
    wet.gain.value = 0.4;
    echo.connect(damp);
    damp.connect(feedback);
    feedback.connect(echo);
    echo.connect(wet);
    wet.connect(master);

    function send(node) { node.connect(master); node.connect(echo); }

    function plink(at, from, to, peak, dur) {
      var osc = ctx.createOscillator();
      var gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(from, at);
      osc.frequency.exponentialRampToValueAtTime(to, at + dur * 0.6);
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(peak, at + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
      osc.connect(gain);
      send(gain);
      osc.start(at);
      osc.stop(at + dur + 0.02);
    }

    function contact(at, peak) {
      var len = Math.floor(ctx.sampleRate * 0.006);
      var buffer = ctx.createBuffer(1, len, ctx.sampleRate);
      var data = buffer.getChannelData(0);
      for (var i = 0; i < len; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / len);
      }
      var src = ctx.createBufferSource();
      src.buffer = buffer;
      var hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 1700;
      var gain = ctx.createGain();
      gain.gain.value = peak;
      src.connect(hp);
      hp.connect(gain);
      send(gain);
      src.start(at);
    }

    contact(t0, 0.3);
    plink(t0, 540, 1580, 0.55, 0.24);          // the drop
    plink(t0 + 0.15, 880, 2100, 0.18, 0.13);   // a smaller satellite droplet
  }

  function waFlush() {
    if (!waQueued || !waCtx || waCtx.state !== 'running') return;
    var req = waQueued;
    waQueued = null;
    if (req.stillWanted && !req.stillWanted()) return;
    waDrop(waCtx, req.volume);
  }

  /* Unlock on the first gesture, and play anything that was queued before it. */
  function waArm() {
    if (waArmed) return;
    waArmed = true;
    ['pointerdown', 'keydown', 'touchstart'].forEach(function (type) {
      document.addEventListener(type, function () {
        var ctx = waContext();
        if (!ctx) return;
        if (ctx.state === 'running') { waFlush(); return; }
        var resuming;
        try { resuming = ctx.resume(); } catch (e) { return; }
        if (resuming && resuming.then) resuming.then(waFlush, function () {});
        else waFlush();
      }, { passive: true, capture: true });
    });
  }

  function waPlayDrop(volume, stillWanted) {
    var ctx = waContext();
    if (!ctx) return;
    if (ctx.state === 'running') { waDrop(ctx, volume); return; }
    waQueued = { volume: volume, stillWanted: stillWanted };
    var resuming;
    try { resuming = ctx.resume(); } catch (e) { return; }
    if (resuming && resuming.then) resuming.then(waFlush, function () {});
    else waFlush();
  }

  function initWhatsappAssistant() {
    var root = $('[data-wa-assist]');
    if (!root || root.dataset.ready) return;

    var panel = $('[data-wa-panel]', root);
    var teaser = $('[data-wa-teaser]', root);
    var launcher = $('[data-wa-launcher]', root);
    var body = $('[data-wa-body]', root);
    var typing = $('[data-wa-typing]', root);
    var result = $('[data-wa-result]', root);
    var actions = $('[data-wa-actions]', root);
    var cta = $('[data-wa-cta]', root);
    var chat = $('[data-wa-chat]', root);
    var steps = $$('[data-wa-step]', root);
    if (!panel || !teaser || !launcher || !body || !result || !steps.length) return;
    root.dataset.ready = 'true';

    var KEY = 'gt_wa_assist';
    var waLink = root.dataset.waLink || '';
    var intro = root.dataset.waIntro || '';
    var fallback = root.dataset.waFallback || '/collections/all';
    var sound = root.dataset.waSound !== 'false';
    var volume = parseInt(root.dataset.waVolume, 10);
    var delay = parseInt(root.dataset.waDelay, 10);
    var remind = parseInt(root.dataset.waRemind, 10);
    var reopen = parseInt(root.dataset.waReopen, 10);
    var editing = !!(window.Shopify && window.Shopify.designMode);
    if (isNaN(delay)) delay = 3;
    if (isNaN(remind)) remind = 7;
    if (isNaN(reopen)) reopen = 25;
    if (isNaN(volume)) volume = 70;
    volume = Math.max(0, Math.min(100, volume)) / 100;
    if (sound) waArm(); // unlock as early as possible, not when the popup is due

    var index = 0;
    var trail = [];
    var lastUrl = '';
    var wantsChat = false;
    var timer = null;
    var engaged = false;

    /* ---- Remembering where the shopper got to ----

       Three separate facts, because they answer different questions:
         localStorage  KEY            - last time it was shown, for the
                                        "show again after N days" rule, which
                                        only applies to a brand new visit.
         sessionStorage KEY_closed    - when it was last dismissed in this
                                        visit, so the reopen countdown
                                        survives the shopper changing page.
         sessionStorage KEY_engaged   - they actually opened the chat, so stop
                                        offering it for the rest of the visit.
                                        Someone who used the thing should never
                                        be nagged by it again.
    */

    function store(area, key, value) {
      try { window[area].setItem(key, value); } catch (e) {}
    }
    function read(area, key) {
      try { return window[area].getItem(key); } catch (e) { return null; }
    }

    function markEngaged() {
      engaged = true;
      store('sessionStorage', KEY + '_engaged', '1');
    }

    function markClosed() {
      store('localStorage', KEY, String(Date.now()));
      store('sessionStorage', KEY + '_closed', String(Date.now()));
    }

    function withinRemindWindow() {
      var at = parseInt(read('localStorage', KEY), 10);
      return !isNaN(at) && Date.now() - at < remind * 86400000;
    }

    /* When the first appearance of a visit is due, or whether it is due at all. */
    function scheduleFirst() {
      if (!editing && read('sessionStorage', KEY + '_engaged')) return;

      var closedAt = parseInt(read('sessionStorage', KEY + '_closed'), 10);
      if (!editing && !isNaN(closedAt)) {
        // Dismissed earlier in this visit - come back once the reopen gap has
        // passed, counted from the dismissal rather than from this page load.
        if (reopen <= 0) return;
        timer = setTimeout(openTeaser, Math.max(0, reopen * 1000 - (Date.now() - closedAt)));
        return;
      }

      if (!editing && withinRemindWindow()) return;
      timer = setTimeout(openTeaser, Math.max(0, delay) * 1000);
    }

    /* ---- Opening and closing ---- */

    function openTeaser() {
      if (panel.classList.contains('is-open')) return;
      teaser.classList.add('is-open');
      launcher.classList.add('has-ping');
      if (!sound) return;
      var asked = Date.now();
      waPlayDrop(volume, function () {
        // Still worth playing while the assistant is on screen - including the
        // case where the queued drop is flushed by the very tap that opens it.
        if (Date.now() - asked > 20000) return false;
        return teaser.classList.contains('is-open') || panel.classList.contains('is-open');
      });
    }

    function closeTeaser(dismissed) {
      teaser.classList.remove('is-open');
      launcher.classList.remove('has-ping');
      if (!dismissed) return;
      markClosed();
      // Swatting it away is not the same as saying no: unless they have
      // actually opened the chat, offer it once more after the reopen gap.
      if (reopen > 0 && !engaged) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(openTeaser, reopen * 1000);
      }
    }

    function openPanel() {
      if (timer) { clearTimeout(timer); timer = null; }
      closeTeaser(false);
      panel.classList.add('is-open');
      launcher.classList.add('is-active');
      launcher.setAttribute('aria-expanded', 'true');
      markEngaged();
      // Hold the first question back until someone is actually looking at it.
      if (!root.dataset.started) {
        root.dataset.started = 'true';
        ask(0);
      }
      var first = $('.wa-step.is-active [data-wa-answer]', panel) || $('[data-wa-close]', panel);
      if (first) first.focus({ preventScroll: true });
    }

    function closePanel() {
      panel.classList.remove('is-open');
      launcher.classList.remove('is-active');
      launcher.setAttribute('aria-expanded', 'false');
      launcher.focus({ preventScroll: true });
    }

    /* ---- The conversation ---- */

    function scrollDown() { body.scrollTop = body.scrollHeight; }

    function think(then) {
      if (!typing) { then(); return; }
      body.appendChild(typing); // always the last thing in the thread
      typing.classList.add('is-on');
      scrollDown();
      setTimeout(function () {
        typing.classList.remove('is-on');
        then();
        scrollDown();
      }, 520);
    }

    function ask(i) {
      index = i;
      steps.forEach(function (step, si) {
        if (si === i) step.classList.add('is-asked', 'is-active');
        else if (si < i) step.classList.remove('is-active');
      });
      if (i >= steps.length) finish();
      scrollDown();
    }

    function waMessage() {
      var lines = trail.map(function (turn) { return '- ' + turn.q + ' ' + turn.a; });
      return (intro ? intro + '\n\n' : '') + lines.join('\n');
    }

    function finish() {
      if (cta) cta.setAttribute('href', lastUrl || fallback);
      if (chat) chat.setAttribute('href', waLink + '?text=' + encodeURIComponent(waMessage()));
      // Someone who asked to talk to a person should see that button first.
      if (actions) actions.classList.toggle('wa-actions--chat-first', wantsChat);
      result.classList.add('is-asked', 'is-active');
      markEngaged();
      markClosed();
    }

    function answer(btn) {
      var step = btn.closest('[data-wa-step]');
      if (!step || !step.classList.contains('is-active')) return;
      var question = $('[data-wa-q]', step);
      var label = btn.textContent.trim();
      var target = (btn.getAttribute('data-wa-target') || '').trim();

      trail.push({ q: question ? question.textContent.trim() : '', a: label });
      if (target.toLowerCase() === 'whatsapp') wantsChat = true;
      // A dash means "no link of its own", so the previous answer's link stands.
      else if (target && target !== '-' && target !== '#') lastUrl = target;

      var said = document.createElement('p');
      said.className = 'wa-msg wa-msg--me';
      said.textContent = label;
      step.parentNode.insertBefore(said, step.nextSibling);
      step.classList.remove('is-active');
      scrollDown();
      think(function () { ask(index + 1); });
    }

    function restart() {
      trail = [];
      lastUrl = '';
      wantsChat = false;
      $$('.wa-msg--me', body).forEach(function (node) { node.parentNode.removeChild(node); });
      steps.forEach(function (step) { step.classList.remove('is-asked', 'is-active'); });
      result.classList.remove('is-asked', 'is-active');
      if (actions) actions.classList.remove('wa-actions--chat-first');
      ask(0);
    }

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-wa-dismiss]')) { closeTeaser(true); return; }
      if (e.target.closest('[data-wa-open]')) { openPanel(); return; }
      if (e.target.closest('[data-wa-close]')) { closePanel(); return; }
      if (e.target.closest('[data-wa-launcher]')) {
        if (panel.classList.contains('is-open')) closePanel();
        else openPanel();
        return;
      }
      if (e.target.closest('[data-wa-restart]')) { restart(); return; }
      if (e.target.closest('[data-wa-chat]')) { markEngaged(); markClosed(); return; } // let the link through
      var pick = e.target.closest('[data-wa-answer]');
      if (pick) answer(pick);
    });

    if (!waEscBound) {
      waEscBound = true;
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var live = $('[data-wa-assist]');
        if (!live) return;
        var livePanel = $('[data-wa-panel]', live);
        var liveTeaser = $('[data-wa-teaser]', live);
        if (livePanel && livePanel.classList.contains('is-open')) $('[data-wa-close]', live).click();
        else if (liveTeaser && liveTeaser.classList.contains('is-open')) $('[data-wa-dismiss]', live).click();
      });
    }

    scheduleFirst();
  }

  /* ---------------- Init ---------------- */

  /* ---------------- Offer popup ---------------- */

  /* Dismissal is keyed by the code, so switching to a new code shows the popup
     to everyone again. Storage can throw (private mode, blocked site data); the
     popup then simply shows on each page load after the delay, which is the
     safe failure. In the theme editor it opens only while its section is
     selected, so it never covers the page being edited. */
  function initOfferPopup() {
    var el = $('[data-offer-popup]');
    if (!el || el.__offer) return;
    el.__offer = true;

    var key = 'gt_offer_' + el.getAttribute('data-offer-code');
    var reshowMs = (parseInt(el.getAttribute('data-offer-reshow'), 10) || 3) * 864e5;
    var releaseFocus = null;

    function open() {
      if (el.classList.contains('is-open')) return;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      scrollLock.on();
      releaseFocus = trapFocus(el);
    }
    function close(remember) {
      if (!el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      scrollLock.off();
      if (releaseFocus) releaseFocus();
      if (remember) {
        try { localStorage.setItem(key, String(Date.now())); } catch (e) {}
      }
    }

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-offer-close]')) { close(true); return; }
      var copy = e.target.closest('[data-offer-copy]');
      if (copy) {
        var code = el.getAttribute('data-offer-code');
        var done = function () {
          copy.classList.add('is-copied');
          setTimeout(function () { copy.classList.remove('is-copied'); }, 2200);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done, function () { selectCode(); });
        } else {
          selectCode();
        }
        return;
      }
      // Following the shop button counts as having seen the offer.
      if (e.target.closest('.offer-popup__cta')) close(true);
    });
    // Without clipboard access, select the code so a long-press copies it.
    function selectCode() {
      var value = $('[data-offer-code-value]', el);
      if (!value || !window.getSelection) return;
      var range = document.createRange();
      range.selectNodeContents(value);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close(true);
    });

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', function (e) {
        if (e.target.contains(el)) open();
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (e.target.contains(el)) close(false);
      });
      return;
    }

    var seen = 0;
    try { seen = parseInt(localStorage.getItem(key), 10) || 0; } catch (e) {}
    if (seen && Date.now() - seen < reshowMs) return;

    var delay = (parseInt(el.getAttribute('data-offer-delay'), 10) || 0) * 1000;
    setTimeout(function () {
      // Don't stack on top of a drawer or modal the shopper already opened;
      // nothing is remembered, so it shows on the next page instead.
      if (document.querySelector('.drawer.is-open, .modal.is-open')) return;
      open();
    }, delay);
  }

  function init() {
    initDrawers();
    initCartEvents();
    initProductForms();
    initVariantPickers();
    initGallery();
    initQuickView();
    initSliders();
    initSlideshows();
    initAnnouncement();
    initSearch();
    initStickyHeader();
    initStickyATC();
    initTabs();
    initCountdowns();
    initViewers();
    initAnimations();
    initFacets();
    initVolume();
    initCODForm();
    initWishlist();
    initRecentlyViewed();
    initVideoCards();
    initLazyVideos();
    initMarquee();
    initShare();
    initNewsletterFocus();
    initBundleBuilder();
    initCardSwatches();
    initRecommendations();
    initBeforeAfter();
    initQuiz();
    initFooterAccordions();
    initMobileNav();
    initWhatsappAssistant();
    initReviewAudio();
    initOfferPopup();
    Cart.get().then(function (cart) { Cart.state = cart; Cart.renderShippingBar(cart); }).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function () {
    initSliders(); initSlideshows(); initAnnouncement(); initCountdowns(); initViewers();
    initAnimations(); initTabs(); initVariantPickers(); initMarquee();
    initBundleBuilder(); initVideoCards(); initLazyVideos(); initRecommendations();
    initBeforeAfter(); initQuiz(); initWhatsappAssistant(); initOfferPopup();
    initReviewAudio(); initGalleryTracks();
  });
  document.addEventListener('shopify:section:select', function (e) {
    var slideshow = e.target.querySelector('[data-slideshow]');
    if (slideshow) { /* pause autoplay while editing */ }
  });
})();
